// Two-level template picker (Marcus, Nov 3 2026): choose the template, then a
// popout lists that template's colour schemes. To add a template, add a family
// here; the results page and the editor both read this list.
import { Check, FileText } from 'lucide-react';
import {
  DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';

export interface TemplateColor { id: string; label: string; swatch: string }
export interface TemplateFamily { id: string; label: string; subtitle: string; colors: TemplateColor[] }

export const TEMPLATE_FAMILIES: TemplateFamily[] = [
  {
    id: 'rdr',
    label: 'RDR Theme',
    subtitle: 'Built from the approved RDR reference resume',
    colors: [
      { id: 'rdr-blue-theme-v3.docx', label: 'Blue (Default)', swatch: '#17375E' },
      { id: 'rdr-blue-theme-v3-burgundy.docx', label: 'Burgundy', swatch: '#7A1F3D' },
      { id: 'rdr-blue-theme-v3-forest.docx', label: 'Forest Green', swatch: '#1F5A3C' },
      { id: 'rdr-blue-theme-v3-teal.docx', label: 'Teal', swatch: '#1F6070' },
      { id: 'rdr-blue-theme-v3-plum.docx', label: 'Plum', swatch: '#5B2A6E' },
      { id: 'rdr-blue-theme-v3-black.docx', label: 'Black', swatch: '#222222' },
    ],
  },
];

export function TemplateFamilyMenu({ selectedId, onPick, disabled }: { selectedId: string; onPick: (id: string) => void; disabled?: boolean }) {
  return (
    <>
      {TEMPLATE_FAMILIES.map((family) => {
        const current = family.colors.find((c) => c.id === selectedId);
        return (
          <DropdownMenuSub key={family.id}>
            <DropdownMenuSubTrigger disabled={disabled} className={`my-1 ${current ? 'bg-purple-100' : ''}`}>
              <FileText className={`h-4 w-4 mr-2 ${current ? 'text-purple-700' : 'text-muted-foreground'}`} />
              <div className="flex flex-col flex-1 min-w-0">
                <span className={`font-medium ${current ? 'text-purple-900' : ''}`}>{family.label}</span>
                <span className={`text-xs ${current ? 'text-purple-700' : 'text-muted-foreground'}`}>
                  {current ? `Colour: ${current.label}` : family.subtitle}
                </span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-52 bg-white z-50 p-1">
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Colour scheme</div>
              {family.colors.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => onPick(c.id)} disabled={disabled} className="gap-2">
                  <span className="h-4 w-4 rounded-full border border-black/10 shrink-0" style={{ backgroundColor: c.swatch }} />
                  <span className={`flex-1 ${c.id === selectedId ? 'font-semibold text-purple-900' : ''}`}>{c.label}</span>
                  {c.id === selectedId && <Check className="h-4 w-4 text-purple-600" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      })}
    </>
  );
}
